// src/components/doctor/VisitsTab.jsx
//
// "Visits" on the doctor's patient record: what happened, newest first. One
// row per SIGNED visit this doctor had with the patient (the backend scopes
// visits and prescriptions to the signing doctor), joined with its
// prescription and the vitals taken at the visit (utils/visitTimeline.js).
// Expanded: the advice the patient saw, the prescription with each medicine's
// status today, and the next steps given.
//
// `rows` come from buildTimeline (the page computes them once for this tab and
// the Vitals tab's "Visit →" links).
//
// Every fetch is its own section (hooks/useDoctorPatientClinicData): a failed
// visits call shows an error with Retry here and nowhere else; a failed
// prescriptions or vitals call degrades the rows and says so.

import React, { useEffect, useMemo, useState } from 'react';
import { useToast } from '../../context/ToastContext';
import { openPrescriptionPdf } from '../../services/prescriptionApi';
import { filterTimeline } from '../../utils/visitTimeline';
import { fmtDayShort, fmtDayFull } from '../../utils/activeMedicines';
import { vitalChips } from '../../utils/consultVitals';
import { frequencyText } from '../../utils/prescription';
import { fmtDate } from '../../hooks/useConsultation';
import { cleanActionItemText } from '../../utils/actionItems';

export const SCOPE_NOTE = 'Showing visits you signed with this patient. Visits by other doctors aren’t included.';

const STATUS_BADGE = { active: 'badge-confirmed', completed: 'badge-unknown', unknown: 'badge-pending' };

function SectionNote({ children, onRetry }) {
  return (
    <div className="alert alert-warning prec-note-alert" role="alert">
      {children}{' '}
      {onRetry && <button type="button" className="btn btn-sm" onClick={onRetry}>Retry</button>}
    </div>
  );
}

function RowChips({ row }) {
  // BP always when recorded; temperature / pulse / SpO₂ when flagged, so the row stays short.
  const chips = vitalChips(row.vitals).filter(c => c.key === 'bp' || (c.key !== 'body' && c.flagged));
  return (
    <div className="consult-chips prec-visit__chips">
      {chips.map(c => (
        <span key={c.key} className={`consult-chip${c.flagged ? ' consult-chip--flag' : ''}`} title={c.flagText || undefined}>
          {c.text}
          {c.flagged && <><span aria-hidden="true">{c.arrow}</span><span className="sr-only"> ({c.flagText})</span></>}
        </span>
      ))}
      {row.medicineCount > 0 && (
        <span className="consult-chip">{row.medicineCount} medicine{row.medicineCount === 1 ? '' : 's'}</span>
      )}
      {row.followUpDay && <span className="consult-chip">Follow-up {fmtDayShort(row.followUpDay)}</span>}
    </div>
  );
}

function Prescription({ rx }) {
  const signedAt = rx.signedAt ? new Date(rx.signedAt) : null;
  return (
    <section className="prec-vx__sec" aria-label={`Prescription ${rx.rxNumber || ''}`.trim()}>
      <div className="prec-vx__head">
        <span>Prescription{rx.rxNumber ? ` · ${rx.rxNumber}` : ''}</span>
        {signedAt && !Number.isNaN(signedAt.getTime()) && (
          <span className="prec-vx__sub">
            signed {signedAt.toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
          </span>
        )}
      </div>
      <table className="prec-table prec-table--cards">
        <thead>
          <tr><th>Medicine</th><th>Dose</th><th>Timing</th><th>Days</th><th>Status</th></tr>
        </thead>
        <tbody>
          {rx.items.map((it, i) => (
            <tr key={it.id || i}>
              <td data-label="Medicine">
                <strong>{it.label || it.drugName}</strong>
                {it.instructions && <div className="prec-vx__sub">{it.instructions}</div>}
              </td>
              <td data-label="Dose">{it.dose || '—'}</td>
              <td data-label="Timing">{it.plainTiming || frequencyText(it.frequency) || it.frequency || '—'}</td>
              <td data-label="Days">{it.durationDays ?? '—'}</td>
              <td data-label="Status">
                <span className={`badge ${STATUS_BADGE[it.status.kind]}`}>{it.status.label}</span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

// Private notes written during the consultation are saved as "Visit <date>: <text>"
// (useConsultation's visitTag); show the ones that belong to this visit's day.
function visitNotes(row, notes) {
  if (!row.appointmentDate) return [];
  const tag = `Visit ${fmtDate(row.appointmentDate)}:`;
  return (notes || []).filter(n => (n.content || '').startsWith(tag))
    .map(n => ({ ...n, text: n.content.slice(tag.length).trim() })).filter(n => n.text);
}

function Expanded({ row, id, notes }) {
  const privateNotes = visitNotes(row, notes);
  return (
    <div className="prec-vx" id={id}>
      <section className="prec-vx__sec" aria-label="Advice given">
        <div className="prec-vx__head">
          <span>Advice given</span>
          <span className="consult-tag consult-tag--patient">Patient saw</span>
        </div>
        <p className="prec-vx__text">{row.advice || 'No advice recorded.'}</p>
      </section>
      {row.rx.length > 0
        ? row.rx.map(rx => <Prescription key={rx.id} rx={rx} />)
        : <p className="prec-vx__sub">No signed prescription for this visit.</p>}
      {privateNotes.length > 0 && (
        <section className="prec-vx__sec" aria-label="Private notes">
          <div className="prec-vx__head">
            <span>Private note{privateNotes.length > 1 ? 's' : ''}</span>
            <span className="consult-tag consult-tag--private">Private · care team only</span>
          </div>
          {privateNotes.map(n => (
            <div key={n.id}>
              <p className="prec-vx__text">{n.text}</p>
              <p className="prec-vx__sub">{[n.authorName, n.createdAt && fmtDate(n.createdAt)].filter(Boolean).join(' · ')}</p>
            </div>
          ))}
        </section>
      )}
      {row.nextSteps.length > 0 && (
        <section className="prec-vx__sec" aria-label="Next steps given">
          <div className="prec-vx__head"><span>Next steps given</span></div>
          <ul className="prec-vx__list">
            {row.nextSteps.map((s, i) => (
              <li key={s.id || i}>
                {cleanActionItemText(s.description)}
                {s.dueDate ? ` (due ${fmtDayShort(String(s.dueDate).slice(0, 10))})` : ''}
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

function VisitRow({ row, expanded, onToggle, onPdf, pdfBusy, notes }) {
  const panelId = `visit-detail-${row.id}`;
  return (
    <article className="prec-visit" id={`visit-${row.id}`} data-testid="visit-row" aria-label={`${row.headline || 'Visit'} on ${fmtDayFull(row.day)}`}>
      <div className="prec-visit__date">
        <strong>{fmtDayFull(row.day) || '—'}</strong>
        <small>{row.type}</small>
      </div>
      <div className="prec-visit__main">
        <h3 className="prec-visit__title" id={`visit-title-${row.id}`} tabIndex={-1}>{row.headline || 'Diagnosis not recorded'}</h3>
        <RowChips row={row} />
        {expanded && <Expanded row={row} id={panelId} notes={notes} />}
      </div>
      <div className="prec-visit__actions">
        {row.pdfRxId && (
          <button type="button" className="btn btn-secondary btn-sm" disabled={pdfBusy} onClick={() => onPdf(row)}
            aria-describedby={`visit-title-${row.id}`}>
            {pdfBusy ? 'Opening…' : 'Prescription PDF'}
          </button>
        )}
        <button type="button" className="btn btn-ghost btn-sm" aria-expanded={expanded} aria-controls={expanded ? panelId : undefined}
          aria-describedby={`visit-title-${row.id}`}
          onClick={() => onToggle(row.id)}>
          <span aria-hidden="true">{expanded ? '▾' : '▸'} </span>{expanded ? 'Collapse' : 'Details'}
        </button>
      </div>
    </article>
  );
}

export default function VisitsTab({
  rows, visits, prescriptions, vitals, focusVisitId, onFocusHandled, onReload, notes,
}) {
  const { toast } = useToast();
  const [query, setQuery] = useState('');
  const [expanded, setExpanded] = useState(() => new Set());
  const [busyId, setBusyId] = useState(null);

  const shown = useMemo(() => filterTimeline(rows, query), [rows, query]);

  // "Open →" from the Last visit tile / "Visit →" in the vitals table: expand that visit and bring it into view.
  useEffect(() => {
    if (!focusVisitId || visits.status === 'loading' || visits.status === 'idle') return;
    const row = rows.find(r => r.id === focusVisitId);
    if (row) {
      setQuery('');
      setExpanded(prev => new Set(prev).add(row.id));
      requestAnimationFrame(() => {
        const el = document.getElementById(`visit-${row.id}`);
        if (el) { el.scrollIntoView({ block: 'start' }); el.querySelector('h3')?.focus?.(); }
      });
    }
    onFocusHandled?.();
  }, [focusVisitId, visits.status, rows, onFocusHandled]);

  const toggle = id => setExpanded(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const openPdf = async row => {
    setBusyId(row.id);
    try {
      const rx = row.rx[0];
      await openPrescriptionPdf(row.pdfRxId, `${rx?.rxNumber || 'prescription'}.pdf`);
    } catch {
      toast.error('Couldn’t open the prescription PDF — please try again');
    } finally {
      setBusyId(null);
    }
  };

  const loading = visits.status === 'loading' || visits.status === 'idle';

  return (
    <div className="card prec-visits" data-testid="visits-panel">
      <div className="prec-toolbar">
        <input
          type="search" className="form-input prec-search" value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder="Search diagnosis or medicine…" aria-label="Search diagnosis or medicine"
        />
        <span className="prec-note" data-testid="visits-scope-note">{SCOPE_NOTE}</span>
      </div>

      {prescriptions.status === 'error' && (
        <SectionNote onRetry={() => onReload('prescriptions')}>Couldn’t load prescriptions, so medicines and PDFs are missing below.</SectionNote>
      )}
      {vitals.status === 'error' && (
        <SectionNote onRetry={() => onReload('vitals')}>Couldn’t load vitals, so readings are missing below.</SectionNote>
      )}

      {loading ? (
        <div className="skeleton-list prec-skeletons" aria-busy="true" aria-label="Loading visits">
          <div className="skeleton skeleton--row" /><div className="skeleton skeleton--row" /><div className="skeleton skeleton--row" />
        </div>
      ) : visits.status === 'error' ? (
        <div className="empty-state prec-empty" role="alert">
          <div className="empty-title">Couldn’t load visits</div>
          <div className="empty-desc">{visits.error} The rest of this patient’s record is unaffected.</div>
          <button type="button" className="btn btn-primary" onClick={() => onReload('visits')}>Retry</button>
        </div>
      ) : rows.length === 0 ? (
        <div className="empty-state prec-empty">
          <div className="empty-title">No signed visits yet</div>
          <div className="empty-desc">Visits appear here once you sign them. Drafts are not shown.</div>
        </div>
      ) : shown.length === 0 ? (
        <div className="empty-state prec-empty">
          <div className="empty-title">No visits match “{query.trim()}”</div>
          <button type="button" className="btn btn-ghost" onClick={() => setQuery('')}>Clear search</button>
        </div>
      ) : (
        <div className="prec-visit-list">
          {shown.map(row => (
            <VisitRow key={row.id} row={row} expanded={expanded.has(row.id)} onToggle={toggle}
              onPdf={openPdf} pdfBusy={busyId === row.id} notes={notes} />
          ))}
        </div>
      )}
    </div>
  );
}
